/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserInfo } from '@/components/Authenticate/useUserInfo';
import { JwtPayload } from '@/types/jwt';
import { Task, TaskList } from '@/types/recordManagement';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';
import {
  BundleType,
  // CommunicationRequestType,
  CreateFHIRBundleParams,
  DocumentReference,
  DocumentReferenceInput,
  DocumentReviewOptions,
  TaskType,
} from '@/types';
// import {
//   AuditEventAgent,
//   AuditEventCode,
//   AuditEventEntity,
//   AuditEventPayload,
// } from '@/types/recordManagement/audit';
import {
  EXCLUDED_STATUSES,
  NONCE_CHARACTERS,
  organisationUUID,
  practitionerUUID,
} from '@/constants';

type ParamValue = string | string[];
type ParamsObject = Record<string, ParamValue>;

interface IssueDetail {
  severity: string;
  code: string;
  details: {
    text: string;
  };
}

interface ExtensionDetail {
  url: string;
  extension: {
    url: string;
    valueId: string;
  }[];
}

interface ParsedError {
  resourceType: string;
  id: string;
  issue: IssueDetail[];
  extension: ExtensionDetail[];
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}



/**
 * Generates a random nonce (number used once) string of a specified length.
 *
 * This function generates a random string that can be used as a nonce for security purposes,
 * such as in OAuth authorization requests. The nonce is composed of uppercase and lowercase
 * letters, as well as digits.
 *
 * @param {number} [length=16] - The length of the generated nonce string. Default is 16 characters.
 * @returns {string} - A randomly generated nonce string of the specified length.
 *
 * @example
 * // Generate a nonce with the default length of 16 characters
 * const nonce = generateNonce();
 * console.log(nonce); // e.g., 'aBcDeFgHiJkLmNoP'
 *
 * // Generate a nonce with a custom length of 32 characters
 * const longNonce = generateNonce(32);
 * console.log(longNonce); // e.g., 'aBcDeFgHiJkLmNoPaBcDeFgHiJkLmNoP'
 */
export const generateNonce = (length: number = 16): string => {
  let nonce = '';

  // Generate the nonce by randomly selecting characters from the defined set
  for (let i = 0; i < length; i++) {
    nonce += NONCE_CHARACTERS.charAt(
      Math.floor(Math.random() * NONCE_CHARACTERS.length)
    );
  }

  return nonce;
};


  // Function to generate a random code_verifier (using crypto for security)
  export function generateCodeVerifier() {
    const array = new Uint32Array(56); // Generates 56 random bytes
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => dec.toString(36)).join(''); // Convert to a string
  }
  
  // Function to hash the code_verifier using SHA-256 and return a base64-url encoded code_challenge
  export async function generateCodeChallenge(codeVerifier: any) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', data); // SHA-256 hash
    const hashArray = Array.from(new Uint8Array(hashBuffer));
  
    const base64 = btoa(String.fromCharCode.apply(null, hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // URL-safe base64
  }

export function extractErrorDetails(errorMessage: string): {
  errorMessage: string;
  traceId: string;
} {
  try {
    // Step 1: Find the JSON part by locating the starting `{` character
    const jsonStartIndex = errorMessage.indexOf('{');

    if (jsonStartIndex === -1) {
      throw new Error('No JSON found in the error message');
    }

    // Step 2: Extract the JSON string
    const jsonString = errorMessage.slice(jsonStartIndex);

    // Step 3: Parse the JSON string
    const parsedError: ParsedError = JSON.parse(jsonString);

    // Step 4: Extract the error message and trace ID (if they exist)
    const errorMessageText =
      parsedError.issue?.[0]?.details?.text ?? 'Unknown error message';
    const traceId =
      parsedError.extension?.[0]?.extension?.find(
        (ext) => ext.url === 'traceId'
      )?.valueId ?? 'No trace ID';

    // Return the extracted details
    return {
      errorMessage: errorMessageText,
      traceId,
    };
  } catch (error) {
    // Handle any parsing or extraction errors
    console.error('Error extracting details:', error);
    return { errorMessage: 'Error parsing the error message', traceId: 'N/A' };
  }
}

export const isTokenExpired = (token: string): boolean => {
  if (!token) {
    return true;
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      console.error('The token is invalid: missing payload');
      return true;
    }

    // Convert Base64URL to Base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    // Decode the payload
    const decoded: JwtPayload = JSON.parse(
      typeof window !== 'undefined'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf-8')
    );

    // Check for expiration
    if (!decoded.exp) {
      console.warn(
        'The token does not have an expiry. Consider adding "exp" to your JWT.'
      );
      return true; // Treat tokens without `exp` as expired for safety
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Error validating JWT token expiry:', error);
    return true; // Treat invalid tokens as expired
  }
};

export const urlParamsToJson = (
  searchParams: string | URLSearchParams
): ParamsObject => {
  // If string is passed, create URLSearchParams object
  const params =
    typeof searchParams === 'string'
      ? new URLSearchParams(searchParams)
      : searchParams;

  const result: ParamsObject = {};

  // Iterate over URLSearchParams
  for (const [key, value] of params) {
    if (result[key]) {
      // If key already exists, convert to array
      result[key] = Array.isArray(result[key])
        ? [...(result[key] as string[]), value]
        : [result[key] as string, value];
    } else {
      result[key] = value;
    }
  }

  return result;
};

export const getNameInitials = (name?: string) =>
  name?.split(' ')
    .reduce((prev, n) => prev + n.charAt(0) + ' ', '')
    .trim();

export const parsePatientName = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patientName: any | string,
  failMessage = 'No name'
) => {
  // On shared page the Public API returns already formatted patient name
  if (typeof patientName === 'string') {
    return patientName.trim();
  }
  if (patientName?.length) {
    const nameObj = patientName[0];
    const givenNames = (nameObj?.given ?? []).join(' ').trim();
    const familyName = nameObj?.family ?? '';
    return `${givenNames} ${familyName}`.trim();
  }
  return failMessage;
};

export const getPractitionerId = (
  response: UserInfo | null
): string | undefined => {
  return response?.additionalInformation?.emr_id ?? undefined;
};

export const getPatientId = (task: Task) => {
  if (!task?.requester?.reference) {
    return null;
  }
  return task.requester.reference.split('/')[1];
};

export const getTaskId = (response: TaskList | null): string | null =>
  response?.[0]?.id ?? null;

export const cleanObject = (
  obj: any,
  keysToRemove: string[] = ['__typename']
): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => cleanObject(item, keysToRemove));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      if (!keysToRemove.includes(key)) {
        acc[key] = cleanObject(obj[key], keysToRemove);
      }
      return acc;
    }, {} as any);
  }
  return obj;
};

const attachAccountsIntoTask = (task: Task) => {
  const accounts = task?.meta?.accounts;
  if (!accounts)
    return {
      ...task,
      meta: {
        ...task.meta,
        accounts: [
          {
            reference: task?.for?.reference,
          },
        ],
      },
    };
  if (!accounts?.[0])
    return {
      ...task,
      meta: {
        ...task.meta,
        accounts: [
          {
            reference: task?.for?.reference,
          },
        ],
      },
    };
  if (accounts?.[0]?.reference !== task?.for?.reference)
    return {
      ...task,
      meta: {
        ...task.meta,
        accounts: [
          {
            reference: task?.for?.reference,
          },
        ],
      },
    };
  return task;
};

export const createTaskPayload = (
  status: string,
  statusCode: string,
  task?: Task,
  practitionerId?: string | undefined,
  output?: any,
  displayName?: string
): Task | null => {
  if (!task) return null;
  const taskWithAccounts = attachAccountsIntoTask(task);
  const cleanedTask = cleanObject(removeNullAndEmptyObjects(taskWithAccounts));
  const taskOutput = EXCLUDED_STATUSES.includes(statusCode)
    ? null
    : cleanedTask?.output;

  return removeNullAndEmptyObjects({
    ...cleanedTask,
    resourceType: 'Task',
    identifier: cleanedTask?.identifier?.map((e: any) => ({ value: e.value })),
    status: status ? status : cleanedTask?.status,
    intent: cleanedTask?.intent,
    businessStatus: {
      coding: [
        {
          code: statusCode,
        },
      ],
    },
    focus: {
      reference: cleanedTask?.focus?.reference,
    },
    for: {
      reference: cleanedTask?.for?.reference,
    },
    requester: {
      reference: cleanedTask?.requester?.reference,
    },
    owner: {
      reference: `${practitionerId}`,
      display: displayName || cleanedTask?.owner?.display || '',
    },
    ...((task?.input || cleanedTask?.input) && {
      input: removeNullAndEmptyObjects(cleanedTask?.input),
    }),
    meta: cleanedTask?.meta,
    ...((output || taskOutput) && {
      output:  output || taskOutput,
    }),
    id: cleanedTask?.id,
  });
};

export function createDocumentReference(
  input: DocumentReferenceInput,
  status?: string,
  practitionerName?: string,
  organizationName?: string
): DocumentReference {
  return {
    resourceType: 'DocumentReference',
    id: input.id,
    status: status ? status : input?.status,
    docStatus: input?.docStatus,
    type: {
      coding: [
        {
          code: input?.type?.code,
          display: input?.type?.display,
        },
      ],
    },
    subject: {
      reference: input?.subject?.reference,
    },
    content: input?.content?.map((c) => ({
      attachment: {
        contentType: c?.attachment?.contentType,
        url: c?.attachment?.url,
        size: c?.attachment?.size,
        title: c?.attachment?.title,
        creation: c?.attachment?.creation || undefined,
        data: c?.attachment?.data,
        hash: c?.attachment?.hash || undefined
      },
    })),
    context: input?.context
      ? {
          related: [
            ...(input?.context?.related?.map((r) => ({
              reference: r?.reference,
            })) || []),

            // Add related Practitioner
            ...(input?.context && organizationName ? [{ reference: organisationUUID }] : []),

            // Add related Organization
            ...(input?.context  && practitionerName? [{ reference: practitionerUUID }] : []),
          ],
          period: input?.context?.period || undefined,
        }
      : undefined,

    meta: input?.meta
      ? {
          tag: input?.meta?.tag?.map((t) => ({
            code: t?.code,
            system: t?.system,
          })),
        }
      : undefined,
  };
}

// export function createAuditEventPayload(
//   agent: AuditEventAgent,
//   entity: AuditEventEntity,
//   action: string,
//   type: AuditEventCode,
//   isSupervisior?: boolean
// ): AuditEventPayload {
//   return {
//     resourceType: 'AuditEvent',
//     type: type,
//     action: action,
//     recorded: new Date().toISOString(),
//     outcome: '0',
//     agent: [
//       {
//         ...agent,
//         type: {
//           coding: [
//             {
//               system:
//                 'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
//               code: isSupervisior ? 'VERF' : 'REVIEWER',
//               display: isSupervisior ? 'Verifier' : 'Reviewer',
//             },
//           ],
//         },
//         role: [
//           {
//             coding: [
//               {
//                 system:
//                   'http://terminology.hl7.org/CodeSystem/contractsignertypecodes',
//                 code: isSupervisior ? 'VERF' : 'REVIEWER',
//                 display: isSupervisior ? 'Verifier' : 'Reviewer',
//               },
//             ],
//           },
//         ],
//       },
//     ],
//     source: {
//       observer: {
//         reference: 'Organization/FluentHealth',
//       },
//       type: [
//         {
//           system: 'http://terminology.hl7.org/CodeSystem/audit-source-type',
//           code: 'fh:console-documentengine',
//           display: 'FH Console Document Management',
//         },
//       ],
//     },
//     entity: [
//       {
//         ...entity,
//         type: {
//           system:
//             'http://terminology.hl7.org/CodeSystem/contractsecurityclassificationcodes',
//           code: 'V',
//           display: 'System Object',
//         },
//         lifecycle: {
//           system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
//           code: 'access',
//           display: 'Access/View Record Lifecycle Event',
//         },
//         securityLabel: [
//           {
//             system:
//               'http://terminology.hl7.org/CodeSystem/v3-ConfidentialityClassification',
//             code: 'R',
//             display: 'restricted',
//           },
//         ],
//       },
//     ],
//   };
// }

export function convertTimestamp(timestamp: number): string {
  if (isNaN(timestamp)) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return format(date, 'MMMM dd, yyyy, h:mm a');
}

export const removeNullAndEmptyObjects = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return;
  }

  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(removeNullAndEmptyObjects)
      .filter(
        (item) =>
          item !== undefined &&
          item !== null &&
          !(typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length === 0)
      );

    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (typeof obj === 'object' && !Array.isArray(obj)) {
    const cleanedObject: any = {};

    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeNullAndEmptyObjects(value);

      const isEmptyObject =
        typeof cleanedValue === 'object' &&
        !Array.isArray(cleanedValue) &&
        cleanedValue !== null &&
        Object.keys(cleanedValue).length === 0;

      if (cleanedValue !== undefined && cleanedValue !== null && !isEmptyObject) {
        cleanedObject[key] = cleanedValue;
      }
    });

    return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
  }

  // Return primitives including 0, false, etc.
  return obj;
};

export const removeTypeName = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(removeTypeName);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)
      .filter((key) => key !== '__typename') // Remove __typename
      .reduce((acc: { [key: string]: any }, key) => {
        acc[key] = removeTypeName(obj[key]); // Recursively process object properties
        return acc;
      }, {});
  }
  return obj; // Return non-object values as is
};

export function createFHIRBundle(params: CreateFHIRBundleParams): BundleType {
  const {
    actionType,
    taskData,
    additionalTaskData,
    documentReferenceData,
    // auditEventData,
    communicationRequestData,
    practitionerName,
    organizationName,
  } = params;

  const bundle: BundleType = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [],
  };
  // Task Resource
  const task: TaskType = {
    resourceType: 'Task',
    id: taskData.id || '',
    status: taskData.status || '',
    businessStatus: taskData.businessStatus || { text: '' },
    identifier: taskData.identifier,
    focus: taskData.focus,
    for: taskData.for,
    requester: taskData.requester,
    owner: taskData.owner,
    relevantHistory: taskData.relevantHistory,
    input: taskData?.input,
    output: taskData.output,
    intent: taskData.intent,
    meta: taskData.meta,
  };

  const additionalTask: TaskType = {
    resourceType: 'Task',
    id: additionalTaskData?.id || '',
    status: 'in-progress',
    businessStatus: additionalTaskData?.businessStatus || { text: '' },
    identifier: additionalTaskData?.identifier,
    focus: additionalTaskData?.focus,
    for: additionalTaskData?.for,
    requester: additionalTaskData?.requester,
    owner: additionalTaskData?.owner,
    relevantHistory: additionalTaskData?.relevantHistory,
    input: additionalTaskData?.input,
    output: additionalTaskData?.output,
    intent: additionalTaskData?.intent,
    meta: additionalTaskData?.meta,
  }

  if(additionalTaskData){
    bundle.entry.push({
      resource: removeNullAndEmptyObjects(additionalTask),
      request: { method: 'PUT', url: `Task/${task.id}` },
    });
  }

  bundle.entry.push({
    resource: removeNullAndEmptyObjects(task),
    request: { method: 'PUT', url: `Task/${task.id}` },
  });

  // pratitioner and organisation
  if (practitionerName) {
    const practitioner = {
      resourceType: 'Practitioner',
      name: [{ text: practitionerName }],
      meta: {
        accounts: [
          {
            reference: taskData?.for?.reference,
          }
        ]
      },
    };

    bundle.entry.push({
      fullUrl: practitionerUUID,
      resource: practitioner,
      request: { method: 'POST', url: 'Practitioner' },
    });
  }

  if (organizationName) {
    const organization = {
      resourceType: 'Organization',
      name: organizationName,
       meta: {
        accounts: [
          {
            reference: taskData?.for?.reference,
          }
        ]
      },
    };

    bundle.entry.push({
      fullUrl: organisationUUID,
      resource: organization,
      request: { method: 'POST', url: 'Organization' },
    });
  }

  if(communicationRequestData && communicationRequestData.length > 0) {

  communicationRequestData?.forEach((resource) => {
    if (resource) {
      bundle.entry.push({
        resource,
        request: { method: 'POST', url: 'CommunicationRequest' }
      });
    }
  });
}
  // AuditEvent
  // if (auditEventData) {
  //   bundle.entry.push({
  //     resource: auditEventData,
  //     request: { method: 'POST', url: 'AuditEvent' },
  //   });
  // }
  if (documentReferenceData && actionType !== 'Escalate') {
    const documentReference = createDocumentReference(
      removeNullAndEmptyObjects(documentReferenceData),
      actionType === 'Reject' ? 'entered-in-error' : '',
      practitionerName,
      organizationName
    );
    bundle.entry.push({
      resource: documentReference,
      request: {
        method: 'PUT',
        url: `DocumentReference/${documentReference.id}`,
      },
    });
  }

    if (actionType === 'Reject') {
      const task: TaskType = {
        resourceType: 'Task',
        id: taskData.id || '',
        status: taskData.status || '',
        businessStatus: {
          coding: [
            {
              code: 'document-engine:workflow:process:completed',
            },
          ],
        },
        identifier: taskData.identifier,
        focus: taskData.focus,
        for: taskData.for,
        requester: taskData.requester,
        owner: taskData.owner,
        relevantHistory: taskData.relevantHistory,
        input: taskData?.input,
        output: taskData.output,
        intent: taskData.intent,
        meta: taskData.meta,
      };
      bundle.entry.push({
        resource: removeNullAndEmptyObjects(task),
        request: { method: 'PUT', url: `Task/${task.id}` },
      });
    }

    return bundle;  
  }
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return dateString;
  return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
};

export const transformEscalationData = (data: Task[]) => {
  const transformedObject = data?.map((item) => {
    const issue = item?.output?.[0]?.type?.coding?.[0]?.display || '';
    const description = item?.output?.[0]?.valueString || '';
    
    // Safely handle the first name and last name, checking for undefined
    const givenName = item?.owner?.resource?.name?.[0]?.given?.join(' ') || '';
    const familyName = item?.owner?.resource?.name?.[0]?.family || '';
    
    return {
      escalatedBy: `${givenName} ${familyName}`.trim() || '', // Join names and handle undefined
      issue: issue,
      dateTime: formatDate(item?.meta?.lastUpdated) || '', // Ensure the date is never undefined
      description: description,
      id: item?.id || '', // Make sure id is always a string or empty string
    };
  });
  return transformedObject;
};


export const getDocumentStatus = (type: string) => {
  const statusMap: Record<DocumentReviewOptions, string> = {
    Approve: 'approved',
    Escalate: 'escalated',
    Reject: 'rejected',
  };

  if (type in statusMap) {
    return `Document ${statusMap[type as DocumentReviewOptions]}`;
  }
  return 'Document';
};

export const isNewDay = () => {
  const storedData = JSON.parse(localStorage.getItem('documentCount') || '{}');
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  // Check if the stored date is different from today
  return storedData.date !== today;
};

  export const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

   export const clearAllObservationsFromLocalStorage = () => {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('observationsFor')) {
          localStorage.removeItem(key);
        }
      });
    };
    
export interface DynamicAuditFields {
  eventType: 'create' | 'update' | 'suspend' | 'reactivate';
  practitionerId: string;
  practitionerName: string;
  ipAddress: string;
  textStatus: string;
  textDiv: string;
}

export function buildAuditEvent({
  eventType,
  practitionerId,
  practitionerName,
  ipAddress,
  textStatus,
  textDiv,
}: DynamicAuditFields) {
  const eventDetailsMap = {
    create: {
      code: 'originate',
      display: 'Originate/Retain Record Lifecycle Event',
      lifecycle:
        'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle|6',
    },
    update: {
      code: 'amend',
      display: 'Amend (Update) Record Lifecycle Event',
      lifecycle:
        'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle|2',
    },
    suspend: {
      code: 'suspend',
      display: 'Deprecate Record Lifecycle Event',
      lifecycle:
        'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle|15',
    },
    reactivate: {
      code: 'reactivate',
      display: 'Re-activate Record Lifecycle Event',
      lifecycle:
        'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle|6',
    },
  };

  const eventDetails = eventDetailsMap[eventType];

  return {
    resourceType: 'AuditEvent',
    type: {
      system: 'http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle',
      code: eventDetails.code,
      display: eventDetails.display,
    },
    recorded: new Date().toISOString(),
    outcome: '0',
    agent: [
      {
        role: [
          {
            coding: [
              {
                system: 'http://fluenthealth.com/CodeSystem/ConsoleRoleFamily',
                code: 'admin',
                display: 'Super Admin',
              },
            ],
          },
        ],
        who: {
          reference: `Practitioner/${practitionerId}`,
        },
        name: practitionerName,
        requestor: true,
        network: {
          address: ipAddress,
          type: '2',
        },
      },
    ],
    source: {
      observer: {
        identifier: { 
          value: 'Organization/FluentHealth',
        },
      },
      type: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/audit-source-type',
          code: 'fh:console-user',
          display: 'FH Console User Management',
        },
      ],
    },
    entity: [
      {
        what: {
          reference: `Practitioner/${practitionerId}`,
        },
        role: {
          system: 'http://fluenthealth.com/CodeSystem/ConsoleRoleFamily',
          code: 'record-reviewer',
          display: 'Records Reviewer',
        },
        type: {
          system: 'http://terminology.hl7.org/CodeSystem/audit-entity-type',
          code: '1',
          display: 'Person',
        },
        lifecycle: {
          system: 'http://terminology.hl7.org/CodeSystem/dicom-audit-lifecycle',
          code: eventDetails.lifecycle.split('|')[1],
          display: eventDetails.display,
        },
        securityLabel: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
            code: 'R',
            display: 'restricted',
          },
        ],
        name: textStatus,
        description: textDiv,
      },
    ],
  };
}

export const onlyValidatorsHaveCheckResultAllowed = (userPermissions: any): boolean => {
  if (!userPermissions) return false;

  const validatorKeys = [
    'validator_documents_approve',
    'validator_documents_modify',
    'validator_documents_reject',
    'validator_documents_view',
    'validator_documents_escalate',
  ];

  return validatorKeys.some(key => userPermissions[key] === 'CHECK_RESULT_ALLOWED');
};


export const onlyReviewerHaveCheckResultAllowed = (userPermissions: any): boolean => {
  if (!userPermissions) return false;

  const reviewerKeys = [
    'reviewer_documents_approve',
    'reviewer_documents_escalate',
    'reviewer_documents_modify',
    'reviewer_documents_reject',
    'reviewer_documents_view',
  ];

  return reviewerKeys.every(key => userPermissions[key] === 'CHECK_RESULT_ALLOWED');
};

export function buildAdvancedAuditLogESQuery({
  search,
  modules,
  status,
  fromDate,
  toDate,
  from = 0,
  size = 10,
  sortField = "recorded",
  sortOrder = "desc"
}: {
  search?: string;
  modules?: string[];
  status?: string[];
  fromDate?: string;
  toDate?: string;
  from?: number;
  size?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
}) {
  const must: any[] = [];

  // Fuzzy text search across all fields
  if (search?.trim()) {
    must.push({
      query_string: {
        query: `*${search.trim()}*`,
        analyze_wildcard: true,
        default_operator: "AND"
      }
    });
  }

  // Filter by source module
  if (modules?.length) {
    must.push({
      terms: {
        "source.type.code.keyword": modules
      }
    });
  }

  // Filter by status keywords
  if (status?.length) {
    const [firstStatus] = status;
    const statusConditions: any[] = [];

    // subtype.code
    statusConditions.push({
      terms: {
        "subtype.code.keyword": status
      }
    });

    // type.display
    statusConditions.push({
      terms: {
        "type.display.keyword": status
      }
    });

    // Match entity.description for keywords like 'approved', 'rejected'
    if (firstStatus) {
      statusConditions.push({
        wildcard: {
          "entity.description.keyword": `*${firstStatus.toLowerCase()}*`
        }
      });
    }

    must.push({
      bool: {
        should: statusConditions,
        minimum_should_match: 1
      }
    });
  }

  // Date range filtering on 'recorded'
  if (fromDate || toDate) {
    const range: Record<string, string> = {};
    if (fromDate) range.gte = `${fromDate}T00:00:00Z`;
    if (toDate) range.lte = `${toDate}T23:59:59Z`;

    must.push({
      range: {
        recorded: range
      }
    });
  }

  // Final query structure
  const query = must.length
    ? { bool: { must } }
    : { match_all: {} };

  return {
    query,
    from,
    size,
    sort: [
      { [sortField]: { order: sortOrder } }
    ]
  };
}

export function buildLonicESQuery(query: string) {
  return {
  query: {
    bool: {
      must: [
        {
          multi_match: {
            query: query,
            type: "bool_prefix",
            fields: [
              "LONG_COMMON_NAME.search",
              "SHORTNAME.search"
            ]
          }
        },
        {
          bool: {
            filter: [
              {range: {COMMON_TEST_RANK: {gt: 0}}}
            ]
          }
        }
      ]
    }
  },
  sort: [
      {COMMON_TEST_RANK: {"order": "asc"}
    }
  ]
}
}

